import React from 'react';

import { getRelativeTime } from '../../shared-functions/DateTime';
import './live-alerts-list.scss';

export interface AlertEntry {
  id: string | number;
  severity: 'critical' | 'warning' | 'info' | string;
  station?: string;
  location?: string; // Fallback or alternative
  message: string;
  title?: string; // Fallback or alternative
  timestamp: string;
  time?: string; // Fallback or alternative
  status?: string;
}

interface LiveAlertsListProperties {
  alerts: AlertEntry[];
}

/**
 * LiveAlertsList - A shared component to display a list of system alerts.
 * 
 * @param props - Component properties.
 * @returns The rendered alert list.
 */
function LiveAlertsList({ alerts }: LiveAlertsListProperties) {
  /**
   * Render alert icon based on severity.
   */
  const renderIcon = (severity: string) => {
    const s = severity.toLowerCase();
    const isCritical = s === 'critical' || s === 'error';
    const isWarning = s === 'warning';

    // Default to info
    let colorClass = 'info';
    let iconPath = 'M12 17V11M12 7H12.01'; // Info icon path
    let strokeColor = '#3B82F6';

    if (isCritical) {
      colorClass = 'error';
      iconPath = 'M12 7V13M12 17H12.01';
      strokeColor = '#F04438';
    } else if (isWarning) {
      colorClass = 'warning';
      iconPath = 'M12 7V13M12 17H12.01';
      strokeColor = '#F79009';
    }

    return (
      <div className={`alert-icon-box ${colorClass}`}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect width="24" height="24" rx="12" fill="currentColor" fillOpacity="0.1" />
          <path d={iconPath} stroke={strokeColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    );
  };

  return (
    <div className="live-alerts-list-container">
      {alerts.map((alert) => {
        const title = alert.message || alert.title;
        const subTitle = alert.station || alert.location;
        const timeDisplay = alert.timestamp ? getRelativeTime(alert.timestamp) : alert.time;

        return (
          <div key={alert.id} className="alert-item">
            {renderIcon(alert.severity)}
            <div className="alert-details">
              <p className="alert-title">{title}</p>
              <div className="alert-meta">
                <span className="alert-time">{timeDisplay}</span>
                {subTitle && (
                  <>
                    <span className="meta-separator">|</span>
                    <span className="alert-location">{subTitle}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default LiveAlertsList;
